import { FC } from 'react';
import scss from './index.module.scss';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  LegendItem,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { Box, Grid, Link, Typography } from '@mui/material';
import { saveAs } from 'file-saver';
import { StyledButton } from '@/components/styled/button';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CustomFormEndPageProps } from '@/components/layouts/layout.props';

const ProductShape: FC<CustomFormEndPageProps> = ({
  scores,
  design,
  jsonData,
}) => {
  ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    {
      id: 'custom_canvas_background_color',
      beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
        ctx!.save();
        ctx!.globalCompositeOperation = 'destination-over';
        ctx!.fillStyle = '#000000';
        ctx!.fillRect(0, 0, chart.width, chart.height);
        ctx!.restore();
      },
    }
  );

  const saveChart = () => {
    //save to png
    const canvasSave = document.getElementById('product-shape-chart');
    // @ts-ignore: Object is possibly 'null'.
    canvasSave.toBlob(function (blob) {
      saveAs(blob, 'product-shape-chart.png');
    });
  };

  // console.log(chart.config.options) to see this object
  const chartData = {
    labels: jsonData.chartCategories,
    datasets: [
      {
        label: 'Shape',
        data: scores,
        backgroundColor: 'rgba(134, 188, 37, 0.2)',
        borderColor: 'rgba(134, 188, 37, 1)',
        color: 'rgba(255, 255, 255, 0.75)',
        borderWidth: 1,
      },
    ],
  };

  // console.log(chart.config.options) to see this object
  const chartOptions: {
    plugins: {
      legend: {
        display: boolean;
      };
    };
    scales: {
      r: {
        min: number;
        max: number;
        ticks: {
          stepSize: number;
          callback: any;
        };
        grid: {
          color: string;
          lineWidth: number;
        };
        pointLabels: any;
      };
    };
  } = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 3,
        ticks: {
          stepSize: 1,
          callback: function (index: number): string {
            switch (index) {
              case 1:
                return 'Learning';
              case 2:
                return 'Experienced';
              case 3:
                return 'Expert';
              default:
                return '';
            }
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.75)',
          lineWidth: 2,
        },
        pointLabels: {
          /* Adjust data label font size according to chart size */
          font: function (context: { chart: { width: any } }) {
            var width = context.chart.width;
            var size = Math.round(width / 48);
            return {
              size: size,
            };
          },
          color: 'rgba(255, 255, 255, 1)',
        },
      },
    },
  };

  return (
    <div
      className='main-end-page-wrapper'
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Grid
        container
        justifyContent='center'
        alignItems='center'
        sx={{
          marginTop: '12.5vh',
          padding: ['5%', '5%', '5%', '0 10%'],
        }}
      >
        <Grid item xs={12} md={4}>
          <Typography variant='h5' color={design.colors.question}>
            Thank you for completing your product shape
          </Typography>
          <StyledButton
            background={design.colors.button}
            $buttonColor={design.colors.buttonText}
            highlight={design.colors.buttonActive}
            className={'save-button'}
            onClick={saveChart}
            style={{
              margin: '5% 0 10% 0',
              fontWeight: 'bold',
              width: '50%',
              fontSize: '12px',
            }}
          >
            SAVE SHAPE
          </StyledButton>
          <Grid container justifyContent='left'>
            <Grid item md={2} alignSelf='center'>
              <MenuBookTwoToneIcon sx={{ color: 'white' }} fontSize='large' />
            </Grid>
            <Grid item md={8}>
              <Typography
                color={design.colors.question}
                fontSize='14px'
                sx={{
                  marginBottom: '5%',
                  fontSize: ['14px', '14px', '16px'],
                }}
              >
                Access education material and course offered by LinkedIn
                Learning to continue building your skill set.
              </Typography>
            </Grid>
            <Grid item md={2}></Grid>
            <Grid item md={2}></Grid>
            <Grid item>
              <Link
                href='https://develop.deloitte.com/consulting-linkedin-learning-launchpad#/subpage/home'
                target='_black'
                rel='noreferrer'
                color={design.colors.button}
                underline='none'
                sx={{
                  display: 'flex',
                  whiteSpace: 'nowrap',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    marginRight: '2%',
                    fontWeight: 'bold',
                    fontSize: ['14px', '14px', '16px'],
                  }}
                >
                  Learn More
                </Typography>
                <OpenInNewIcon
                  sx={{ color: design.colors.button }}
                  fontSize='small'
                />
              </Link>
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={1}></Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ width: ['auto', 'auto', '85%'] }}>
            <Radar
              id='product-shape-chart'
              data={chartData}
              options={chartOptions}
            />
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export default ProductShape;
